/**
 * DisplayPanelManagement.js
 *
 * Manage display panels: list, create, edit, delete and print QR codes.
 * Purpose of this page is primarily QR printing for showroom panels.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import QRCodeLib from 'qrcode';
import QRCodeSVG from 'react-qr-code';
import { jsPDF } from 'jspdf';
import Swal from 'sweetalert2';
import {
  FaQrcode,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaBox,
  FaTimes,
  FaCopy,
  FaEye,
  FaDownload,
  FaFilePdf,
  FaLayerGroup,
  FaListAlt
} from 'react-icons/fa';

const API = () => process.env.REACT_APP_API_BASE_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export default function DisplayPanelManagement() {
  const [panels, setPanels] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [formName, setFormName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // id -> item, for review chips
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [searchingItems, setSearchingItems] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [itemHasMore, setItemHasMore] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showQRModal, setShowQRModal] = useState(false);
  const [qrPanel, setQrPanel] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Dark mode state (global, persisted in localStorage)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true' || savedMode === '"true"';
  });

  // Sync dark mode with localStorage (poll for cross-component changes)
  useEffect(() => {
    const sync = () => {
      const savedMode = localStorage.getItem('darkMode');
      setIsDarkMode(savedMode === 'true' || savedMode === '"true"');
    };
    const interval = setInterval(sync, 500);
    return () => clearInterval(interval);
  }, []);

  const itemSearchRef = useRef(null);
  const itemTermRef = useRef(''); // last debounced term, guards stale responses
  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Summary stats derived from loaded panels
  const summary = panels.reduce(
    (acc, panel) => {
      acc.panels += 1;
      acc.items += panel.itemCount || 0;
      acc.scans += panel.scanCount || 0;
      acc.views += panel.viewCount || 0;
      return acc;
    },
    { panels: 0, items: 0, scans: 0, views: 0 },
  );

  const fetchPanels = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API()}/panels`, {
        headers: authHeaders(),
        params: searchTerm ? { search: searchTerm } : {},
      });
      if (res.data?.success) {
        setPanels(res.data.panels || []);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: res.data?.message || 'Failed to load display panels',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error('Error fetching panels:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load display panels',
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPanels();
  }, [fetchPanels]);

  // Debounced server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPanels(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search, fetchPanels]);

  // Debounced item search inside the panel form.
  // Empty term browses all items; results paginate via infinite scroll.
  const searchItems = useCallback(
    async (term, page = 1, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setSearchingItems(true);
      }
      try {
        const params = { page, limit: 20 };
        if (term.trim()) {
          params.search = term.trim();
        }
        const res = await axios.get(`${API()}/showroom/items`, {
          headers: authHeaders(),
          params,
        });
        if (itemTermRef.current !== term) return; // stale response, ignore
        if (res.data?.success) {
          setItemResults((prev) =>
            append ? [...prev, ...(res.data.items || [])] : res.data.items || [],
          );
          setItemHasMore(res.data.pagination?.hasNextPage || false);
        } else {
          if (!append) setItemResults([]);
          setItemHasMore(false);
        }
      } catch (error) {
        console.error('Error searching items:', error);
        if (itemTermRef.current !== term) return;
        if (!append) setItemResults([]);
        setItemHasMore(false);
      } finally {
        setSearchingItems(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (itemSearchRef.current) {
      clearTimeout(itemSearchRef.current);
    }
    itemSearchRef.current = setTimeout(() => {
      itemTermRef.current = itemSearch;
      searchItems(itemSearch, 1, false);
    }, 400);
    return () => {
      if (itemSearchRef.current) {
        clearTimeout(itemSearchRef.current);
      }
    };
  }, [itemSearch, searchItems]);

  // Infinite scroll: load the next page when the list is scrolled near the end
  const loadMoreItems = () => {
    if (loadingMore || searchingItems || !itemHasMore) return;
    const nextPage = Math.floor(itemResults.length / 20) + 1;
    searchItems(itemSearch, nextPage, true);
  };
  loadMoreRef.current = loadMoreItems;

  // Auto-load next page when the sentinel scrolls into view (more reliable
  // than scroll math alone — covers outer-modal scrolling and short lists)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !el.parentElement) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && loadMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { root: el.parentElement, threshold: 0.95 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [searchingItems, itemHasMore]);

  const handleItemScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      loadMoreItems();
    }
  };

  const openCreate = () => {
    setEditingPanel(null);
    setFormName('');
    setSelectedIds([]);
    setSelectedItems({});
    setItemSearch('');
    setItemResults([]);
    setShowFormModal(true);
    searchItems('', 1, false);
  };

  const openEdit = (panel) => {
    setEditingPanel(panel);
    setFormName(panel.name || '');
    setSelectedIds([]);
    setSelectedItems({});
    setItemSearch('');
    setItemResults([]);
    setShowFormModal(true);
    searchItems('', 1, false);
    loadPanelItems(panel._id);
  };

  const loadPanelItems = async (panelId) => {
    try {
      const res = await axios.get(`${API()}/panels/${panelId}`, {
        headers: authHeaders(),
      });
      if (res.data?.success && res.data.panel) {
        const items = res.data.panel.items || [];
        setSelectedIds(items.map((item) => item._id));
        setSelectedItems(
          Object.fromEntries(items.map((item) => [item._id, item])),
        );
      }
    } catch (error) {
      console.error('Error loading panel items:', error);
    }
  };

  const toggleItem = (item) => {
    const itemId = item._id;
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (next[itemId]) {
        delete next[itemId];
      } else {
        next[itemId] = item;
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedItems({});
  };

  const handleSave = async () => {
    const name = formName.trim();
    if (!name) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Name',
        text: 'Please enter a name for the display panel.',
        timer: 3000,
        showConfirmButton: false,
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#111827',
      });
      return;
    }
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please select at least one item for the panel.',
        timer: 3000,
        showConfirmButton: false,
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#111827',
      });
      return;
    }

    setSaving(true);
    const payload = { name, itemIds: selectedIds };
    try {
      let res;
      if (editingPanel) {
        res = await axios.put(`${API()}/panels/${editingPanel._id}`, payload, {
          headers: authHeaders(),
        });
      } else {
        res = await axios.post(`${API()}/panels`, payload, { headers: authHeaders() });
      }
      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: editingPanel ? 'Display panel updated successfully.' : 'Display panel created successfully.',
          timer: 1600,
          showConfirmButton: false,
          background: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#f3f4f6' : '#111827',
        });
        setShowFormModal(false);
        fetchPanels(search.trim());
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: res.data?.message || 'Failed to save display panel',
          timer: 3000,
          showConfirmButton: false,
          background: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#f3f4f6' : '#111827',
        });
      }
    } catch (error) {
      console.error('Error saving panel:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to save display panel',
        timer: 3000,
        showConfirmButton: false,
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#111827',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (panel) => {
    Swal.fire({
      title: 'Delete Panel?',
      text: `Are you sure you want to delete "${panel.name}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      background: isDarkMode ? '#1f2937' : '#ffffff',
      color: isDarkMode ? '#f3f4f6' : '#111827',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await axios.delete(`${API()}/panels/${panel._id}`, {
          headers: authHeaders(),
        });
        if (res.data?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Display panel deleted successfully.',
            timer: 1600,
            showConfirmButton: false,
            background: isDarkMode ? '#1f2937' : '#ffffff',
            color: isDarkMode ? '#f3f4f6' : '#111827',
          });
          fetchPanels(search.trim());
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: res.data?.message || 'Failed to delete display panel',
            timer: 3000,
            showConfirmButton: false,
            background: isDarkMode ? '#1f2937' : '#ffffff',
            color: isDarkMode ? '#f3f4f6' : '#111827',
          });
        }
      } catch (error) {
        console.error('Error deleting panel:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete display panel',
          timer: 3000,
          showConfirmButton: false,
          background: isDarkMode ? '#1f2937' : '#ffffff',
          color: isDarkMode ? '#f3f4f6' : '#111827',
        });
      }
    });
  };

  // Generate PNG data URL for the panel QR, then open the modal
  const openQR = async (panel) => {
    try {
      const dataUrl = await QRCodeLib.toDataURL(panel._id, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrCodeUrl(dataUrl);
      setQrPanel(panel);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to generate QR code',
        timer: 3000,
        showConfirmButton: false,
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#111827',
      });
    }
  };

  const copyPanelId = async (panelId) => {
    try {
      await navigator.clipboard.writeText(panelId);
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Panel ID copied to clipboard.',
        timer: 1200,
        showConfirmButton: false,
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#f3f4f6' : '#111827',
      });
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  // Download QR as PNG
  const handleDownloadQR = () => {
    if (!qrCodeUrl || !qrPanel) return;
    const link = document.createElement('a');
    link.download = `panel-${qrPanel.name.replace(/[^a-z0-9-_]/gi, '_')}-qr.png`;
    link.href = qrCodeUrl;
    link.click();
  };

  // Convert the rendered SVG QR to a PNG data URL (used for PDF export)
  const svgToPngDataUrl = () => {
    const svg = document.getElementById('panel-qr-code-svg');
    if (!svg) return null;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = 'data:image/svg+xml;base64,' + window.btoa(unescape(encodeURIComponent(svgData)));
    });
  };

  const downloadQROnlyPdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65],
    });
    pdf.addImage(qrImageData, 'PNG', 5, 15, 35, 35);
    const panelName = qrPanel?.name || 'Panel';
    pdf.save(`${panelName}_QR.pdf`);
  };

  const downloadQRWithNamePdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65],
    });

    const nameText = qrPanel?.name || 'Panel';
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const nameWidth = pdf.getTextWidth(nameText);
    pdf.text(nameText, (45 - nameWidth) / 2, 8);

    const qrSize = 25;
    const qrX = (45 - qrSize) / 2;
    const qrY = 12;
    pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);

    const arrowY = qrY + qrSize + 4;
    const arrowX = 45 / 2;
    pdf.setLineWidth(0.5);
    pdf.line(arrowX, arrowY, arrowX, arrowY + 3);
    pdf.line(arrowX - 1, arrowY + 2, arrowX, arrowY + 3);
    pdf.line(arrowX + 1, arrowY + 2, arrowX, arrowY + 3);

    const itemNameText = qrPanel?.name || 'Panel';
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    const maxWidth = 38;
    const lines = pdf.splitTextToSize(itemNameText, maxWidth);
    const lineHeight = 3.0;
    const startY = arrowY + 8;
    lines.forEach((line, index) => {
      const lineWidth = pdf.getTextWidth(line);
      const lineX = (45 - lineWidth) / 2;
      pdf.text(line, lineX, startY + index * lineHeight);
    });

    pdf.save(`${qrPanel?.name || 'Panel'}_QR_with_name.pdf`);
  };

  const downloadQRPdf = async (includeName = false) => {
    const pngDataUrl = await svgToPngDataUrl();
    if (!pngDataUrl) return;
    if (includeName) {
      downloadQRWithNamePdf(pngDataUrl);
    } else {
      downloadQROnlyPdf(pngDataUrl);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  // Resolve first image id (handles populated object or raw string)
  const getItemImageId = (item) => {
    const first = item?.imageIds?.[0];
    return first ? (typeof first === 'object' ? first._id : first) : null;
  };

  const isItemInStock = (item) =>
    (item?.totalStock?.quantity ?? item?.closingBalance ?? 0) > 0;

  // Reusable class helpers for dark mode
  const dm = {
    page: isDarkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    heading: isDarkMode ? 'text-gray-100' : 'text-gray-800',
    subheading: isDarkMode ? 'text-gray-400' : 'text-gray-500',
    input:
      isDarkMode
        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
        : 'bg-white border-gray-300 text-gray-900',
    label: isDarkMode ? 'text-gray-300' : 'text-gray-600',
    tableHead:
      isDarkMode
        ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300'
        : 'bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-600',
    tableRow: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    divider: isDarkMode ? 'divide-gray-700' : 'divide-gray-100',
    modal: isDarkMode ? 'bg-gray-800' : 'bg-white',
    modalBorder: isDarkMode ? 'border-gray-700' : 'border-gray-200',
  };

  return (
    <div className={`min-h-screen py-4 sm:py-6 transition-colors duration-200 ${dm.page}`}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Gradient Header */}
        <div className={`rounded-xl shadow-lg mb-6 overflow-hidden ${
          isDarkMode
            ? 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800'
            : 'bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-700'
        }`}>
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`p-3 sm:p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'}`}>
                  <FaLayerGroup className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1">
                    Display Panel Management
                  </h1>
                  <p className="text-xs sm:text-sm text-white text-opacity-90">
                    Create showroom panels, manage items and print QR codes for scanning
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={openCreate}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors shadow-md font-semibold"
                >
                  <FaPlus size={14} />
                  New Panel
                </button>
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-6">
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg px-3 py-2.5">
                <p className="text-xs text-white text-opacity-80 font-medium">Panels</p>
                <p className="text-xl sm:text-2xl font-bold text-white">{summary.panels}</p>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg px-3 py-2.5">
                <p className="text-xs text-white text-opacity-80 font-medium">Items</p>
                <p className="text-xl sm:text-2xl font-bold text-white">{summary.items}</p>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg px-3 py-2.5">
                <p className="text-xs text-white text-opacity-80 font-medium">Total Scans</p>
                <p className="text-xl sm:text-2xl font-bold text-white">{summary.scans}</p>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg px-3 py-2.5">
                <p className="text-xs text-white text-opacity-80 font-medium">Total Views</p>
                <p className="text-xl sm:text-2xl font-bold text-white">{summary.views}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className={`rounded-xl shadow-lg border mb-6 overflow-hidden ${dm.card}`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-gray-700 to-gray-800' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
            <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              <FaSearch className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} size={16} />
              Search Panels
            </h2>
          </div>
          <div className="p-6">
            <div className="relative group">
              <FaSearch className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors ${
                isDarkMode ? 'text-gray-400 group-focus-within:text-blue-400' : 'text-gray-400 group-focus-within:text-blue-600'
              }`} size={14} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search panels by name..."
                className={`w-full pl-11 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${dm.input}`}
              />
            </div>
          </div>
        </div>

        {/* Panels table */}
        <div className={`${dm.card} rounded-xl shadow-lg overflow-hidden`}>
          {loading && panels.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : panels.length === 0 ? (
            <div className="text-center py-16">
              <FaBox className={`mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={40} />
              <p className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No display panels found</p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {search ? 'Try a different search term' : 'Create your first display panel'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className={`${dm.tableHead} text-left text-xs font-semibold uppercase tracking-wider`}>
                    <th className="px-4 py-3.5">Panel Name</th>
                    <th className="px-4 py-3.5 text-center">Items</th>
                    <th className="px-4 py-3.5 text-center">Scans</th>
                    <th className="px-4 py-3.5 text-center">Views</th>
                    <th className="px-4 py-3.5">Created</th>
                    <th className="px-4 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${dm.divider}`}>
                  {panels.map((panel) => (
                    <tr key={panel._id} className={`${dm.tableRow} transition-colors`}>
                      <td className="px-4 py-3.5">
                        <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{panel.name}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-semibold">
                          <FaBox size={10} />
                          {panel.itemCount || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sky-50 text-sky-700 rounded-full text-xs font-semibold">
                          <FaQrcode size={10} />
                          {panel.scanCount || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
                          <FaEye size={10} />
                          {panel.viewCount || 0}
                        </span>
                      </td>
                      <td className={`px-4 py-3.5 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatDate(panel.createdAt)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openQR(panel)}
                            title="Show QR Code"
                            className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                          >
                            <FaQrcode size={16} />
                          </button>
                          <button
                            onClick={() => openEdit(panel)}
                            title="Edit Panel"
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(panel)}
                            title="Delete Panel"
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className={`${dm.modal} rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${dm.modalBorder}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                <FaLayerGroup className="text-purple-600" size={18} />
                {editingPanel ? 'Edit Display Panel' : 'New Display Panel'}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-semibold ${dm.label} mb-1.5`}>
                  Panel Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Wall Display - Main Showroom"
                  maxLength={200}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${dm.input}`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`text-sm font-semibold flex items-center gap-1.5 ${dm.label}`}>
                    <FaListAlt className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} size={13} />
                    Select Items
                  </label>
                  <div className="flex items-center gap-2">
                    {selectedIds.length > 0 && (
                      <button
                        onClick={clearSelection}
                        className={`text-xs font-semibold underline ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
                      >
                        Clear all
                      </button>
                    )}
                    <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-semibold">
                      {selectedIds.length} selected
                    </span>
                  </div>
                </div>
                <input
                  type="text"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search items by name, part no, HSN..."
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${dm.input}`}
                />

                {/* Selected items review chips */}
                {Object.keys(selectedItems).length > 0 && (
                  <div className={`mt-2 flex flex-wrap gap-1.5 ${isDarkMode ? '' : ''}`}>
                    {Object.values(selectedItems).map((item) => (
                      <span
                        key={item._id}
                        className={`inline-flex items-center gap-1.5 pl-1 pr-1.5 py-1 rounded-full text-xs font-medium border ${
                          isDarkMode
                            ? 'bg-indigo-900/40 border-indigo-700 text-indigo-200'
                            : 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 ${isDarkMode ? 'bg-gray-700' : ''}`}>
                          {getItemImageId(item) ? (
                            <img
                              src={`${API()}/tally/images/${getItemImageId(item)}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : null}
                        </span>
                        <span className="max-w-[160px] truncate">{item.Name || 'Unknown Item'}</span>
                        <button
                          onClick={() => toggleItem(item)}
                          title="Remove"
                          className={`p-0.5 rounded-full hover:bg-black/10 transition-colors ${isDarkMode ? 'hover:bg-white/20' : ''}`}
                        >
                          <FaTimes size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div
                className={`max-h-56 overflow-y-auto border rounded-lg divide-y ${isDarkMode ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}
                onScroll={handleItemScroll}
              >
                {searchingItems ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                  </div>
                ) : itemResults.length === 0 ? (
                  <p className={`text-center text-sm py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    {itemSearch.trim()
                      ? 'No items match your search'
                      : 'Loading items...'}
                  </p>
                ) : (
                  <>
                    {itemResults.map((item) => {
                      const isSelected = selectedIds.includes(item._id);
                      const imageId = getItemImageId(item);
                      const inStock = isItemInStock(item);
                      return (
                        <button
                          key={item._id}
                          onClick={() => toggleItem(item)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-l-4 ${
                            isSelected
                              ? isDarkMode
                                ? 'bg-indigo-900/40 border-indigo-500'
                                : 'bg-indigo-50 border-indigo-500'
                              : isDarkMode
                                ? 'hover:bg-gray-700 border-transparent'
                                : 'hover:bg-gray-50 border-transparent'
                          }`}
                        >
                          <span className={`w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {imageId ? (
                              <img
                                src={`${API()}/tally/images/${imageId}`}
                                alt={item.Name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FaBox className={`w-full h-full p-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`} />
                            )}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`block text-sm font-medium truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                              {item.Name || 'Unknown Item'}
                            </span>
                            <span className={`block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.partNo ? `Part: ${item.partNo}` : 'No part no'}
                            </span>
                          </span>
                          <span
                            className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              inStock
                                ? 'bg-green-100 text-green-700'
                                : isDarkMode
                                  ? 'bg-gray-700 text-gray-400'
                                  : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {inStock ? 'In stock' : 'Out'}
                          </span>
                          <span
                            className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded border-2 text-xs font-bold ${
                              isSelected
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : isDarkMode
                                  ? 'border-gray-500 text-transparent'
                                  : 'border-gray-300 text-transparent'
                            }`}
                          >
                            ✓
                          </span>
                        </button>
                      );
                    })}
                    {loadingMore ? (
                      <div className="flex justify-center items-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                        <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Loading more...
                        </span>
                      </div>
                    ) : !itemHasMore ? (
                      <p className={`text-center text-xs py-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                        All results loaded
                      </p>
                    ) : null}
                    <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
                  </>
                )}
              </div>

              {itemHasMore && (
                <button
                  onClick={loadMoreItems}
                  disabled={loadingMore}
                  className="mt-2 w-full py-2 text-sm rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading…' : 'Load more items'}
                </button>
              )}
            </div>

            <div className={`flex justify-end gap-3 px-6 py-4 border-t ${dm.modalBorder}`}>
              <button
                onClick={() => setShowFormModal(false)}
                className={`px-4 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : editingPanel ? 'Save Panel' : 'Create Panel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {showQRModal && qrPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className={`${dm.modal} rounded-xl shadow-2xl w-full max-w-sm`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${dm.modalBorder}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} flex items-center gap-2`}>
                <FaQrcode className="text-purple-600" />
                Panel QR Code
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Hidden SVG QR code for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeSVG id="panel-qr-code-svg" value={qrPanel._id} size={200} bgColor="#ffffff" fgColor="#000000" />
            </div>

            <div className="p-6 text-center space-y-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block shadow-sm">
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="Panel QR Code" className="w-48 h-48" />
                ) : (
                  <QRCodeSVG value={qrPanel._id} size={192} bgColor="#ffffff" fgColor="#000000" />
                )}
              </div>
              <div>
                <h4 className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-1 truncate w-full`} title={qrPanel.name}>
                  {qrPanel.name}
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                  Scan with the Tally Maestro app to view all items
                </p>
                <div className="flex items-center justify-center gap-2">
                  <code className={`px-2.5 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {qrPanel._id}
                  </code>
                  <button
                    onClick={() => copyPanelId(qrPanel._id)}
                    className={`p-1.5 rounded transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    title="Copy Panel ID"
                  >
                    <FaCopy size={12} />
                  </button>
                </div>
                <div className="flex gap-2 justify-center mt-3">
                  <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-semibold">
                    {qrPanel.itemCount || 0} Items
                  </span>
                  <span className="px-2.5 py-1 bg-sky-50 text-sky-700 rounded-full text-xs font-semibold">
                    {qrPanel.scanCount || 0} Scans
                  </span>
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
                    {qrPanel.viewCount || 0} Views
                  </span>
                </div>
              </div>

              {/* Download actions */}
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={handleDownloadQR}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                >
                  <FaDownload className="w-4 h-4" />
                  <span>PNG</span>
                </button>
                <button
                  onClick={() => downloadQRPdf(false)}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                >
                  <FaFilePdf className="w-4 h-4" />
                  <span>PDF</span>
                </button>
                <button
                  onClick={() => downloadQRPdf(true)}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                >
                  <FaFilePdf className="w-4 h-4" />
                  <span>PDF + Name</span>
                </button>
              </div>
            </div>
            <div className={`px-6 py-4 border-t ${dm.modalBorder} flex justify-end`}>
              <button
                onClick={() => setShowQRModal(false)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
